import React, { FC, useCallback } from 'react';
import AssignTo, { IAssignToProps } from 'src/shared/AssignTo';
import { useUpdateBidTasks } from 'src/models/bids/BidTask/hooks';
import { useWsUsers } from 'src/models/workspace/hooks';
import { trackBTAssignTask } from 'src/segment/events';
import { BidQuestionType } from 'src/models/bids/BidFull/types';
import { PickerMode } from '../TaskFulfillmentPicker';
import { BidTask } from 'src/models/bids/BidTask';

interface IProps<T extends PickerMode> extends IAssignToProps<T> {
  questions: BidTask[];
  groupId: string;
  type?: T extends 'bulk' ? undefined : BidQuestionType;
  eventSource?: string;
  wsId?: string;
  bidId: string;
  assignToMode: T;
}

export const AssignToQuestion: FC<IProps<'single'> | IProps<'bulk'>> = ({
  size = 'm',
  mode = 'short',
  ...restProps
}) => {
  const { questions, groupId, wsId, bidId, type, assignToMode } = restProps;
  const [updateTasks, { loading }] = useUpdateBidTasks();
  const wsUsers = useWsUsers(wsId);
  const onUpdateTask = useCallback(
    (assignedTo: string | null) => {
      updateTasks({
        questions: questions.filter(item => item.assignedTo?.email !== assignedTo),
        groupId,
        bidId,
        assignedTo
      });
    },
    [bidId, groupId, questions, updateTasks]
  );

  const onAssign = useCallback(
    (email: string) => {
      trackBTAssignTask(assignToMode, groupId, bidId, type);
      onUpdateTask(email);
    },
    [onUpdateTask, groupId, bidId, type, assignToMode]
  );

  const onUnAssign = useCallback(() => {
    onUpdateTask(null);
  }, [onUpdateTask]);

  return (
    <AssignTo
      onAssign={onAssign}
      onUnAssign={onUnAssign}
      isUpdating={loading}
      size={size}
      mode={mode}
      users={wsUsers}
      {...restProps}
    />
  );
};

export default AssignToQuestion;
